import { useState } from 'react';
import { X, Eye, EyeOff, Lock, CheckCircle, XCircle } from 'lucide-react';
import ThreeDotLoading from 'components/ui/ThreeDotLoading';

const UpdatePasswordModal = ({ isOpen, onClose, onConfirm, isLoading }) => {
  const [formData, setFormData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [showPasswords, setShowPasswords] = useState({
    old: false,
    new: false,
    confirm: false,
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Password validation rules
  const validatePassword = (password) => {
    const rules = {
      minLength: password.length >= 8,
      maxLength: password.length <= 16,
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasDigit: /\d/.test(password),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    };
    return rules;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleBlur = (field) => {
    setTouched((prev) => ({
      ...prev,
      [field]: true,
    }));
    validateField(field);
  };

  const validateField = (field) => {
    let error = '';

    switch (field) {
      case 'oldPassword':
        if (!formData.oldPassword) {
          error = 'Old password is required';
        }
        break;

      case 'newPassword':
        if (!formData.newPassword) {
          error = 'New password is required';
        } else {
          const rules = validatePassword(formData.newPassword);
          if (!rules.minLength) {
            error = 'Password must be at least 8 characters';
          } else if (!rules.maxLength) {
            error = 'Password must be at most 16 characters';
          } else if (!rules.hasUpperCase) {
            error = 'Password must contain at least one uppercase letter';
          } else if (!rules.hasLowerCase) {
            error = 'Password must contain at least one lowercase letter';
          } else if (!rules.hasDigit) {
            error = 'Password must contain at least one digit';
          } else if (!rules.hasSpecialChar) {
            error = 'Password must contain at least one special character';
          } else if (formData.oldPassword && formData.newPassword === formData.oldPassword) {
            error = 'New password must be different from old password';
          }
        }
        break;

      case 'confirmPassword':
        if (!formData.confirmPassword) {
          error = 'Please confirm your password';
        } else if (formData.confirmPassword !== formData.newPassword) {
          error = 'Passwords do not match';
        }
        break;

      default:
        break;
    }

    setErrors((prev) => ({
      ...prev,
      [field]: error,
    }));

    return error === '';
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.oldPassword) {
      newErrors.oldPassword = 'Old password is required';
    }

    if (!formData.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else {
      const rules = validatePassword(formData.newPassword);
      if (
        !rules.minLength ||
        !rules.hasUpperCase ||
        !rules.hasLowerCase ||
        !rules.hasDigit ||
        !rules.hasSpecialChar
      ) {
        newErrors.newPassword = 'Password does not meet requirements';
      } else if (formData.newPassword === formData.oldPassword) {
        newErrors.newPassword = 'New password must be different from old password';
      }
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.confirmPassword !== formData.newPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Mark all fields as touched
    setTouched({
      oldPassword: true,
      newPassword: true,
      confirmPassword: true,
    });

    if (validateForm()) {
      // Submit the form
      const res = await onConfirm(formData);
      if (res) {
        handleClose();
      }
    }
  };

  const handleClose = () => {
    setFormData({
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
    setErrors({});
    setTouched({});
    setShowPasswords({
      old: false,
      new: false,
      confirm: false,
    });
    onClose();
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const passwordRules = formData.newPassword ? validatePassword(formData.newPassword) : null;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Lock className="w-5 h-5 text-blue-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-800">Update Password</h2>
          </div>
          <button
            onClick={handleClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Old Password */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Old Password <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type={showPasswords.old ? 'text' : 'password'}
                name="oldPassword"
                value={formData.oldPassword}
                onChange={handleChange}
                onBlur={() => handleBlur('oldPassword')}
                className={`w-full px-4 py-2.5 pr-10 border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
                  errors.oldPassword && touched.oldPassword
                    ? 'border-red-500 focus:ring-red-200'
                    : 'border-gray-300 focus:ring-blue-200 focus:border-blue-500'
                }`}
                placeholder="Enter old password"
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('old')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPasswords.old ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {errors.oldPassword && touched.oldPassword && (
              <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                <XCircle className="w-4 h-4" />
                {errors.oldPassword}
              </p>
            )}
          </div>

          {/* New Password */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              New Password <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type={showPasswords.new ? 'text' : 'password'}
                name="newPassword"
                value={formData.newPassword}
                onChange={handleChange}
                onBlur={() => handleBlur('newPassword')}
                className={`w-full px-4 py-2.5 pr-10 border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
                  errors.newPassword && touched.newPassword
                    ? 'border-red-500 focus:ring-red-200'
                    : 'border-gray-300 focus:ring-blue-200 focus:border-blue-500'
                }`}
                placeholder="Enter new password"
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('new')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPasswords.new ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {errors.newPassword && touched.newPassword && (
              <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                <XCircle className="w-4 h-4" />
                {errors.newPassword}
              </p>
            )}

            {/* Password Requirements */}
            {formData.newPassword && (
              <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-xs font-medium text-gray-700 mb-2">Password Requirements:</p>
                <ul className="space-y-1">
                  <li className="flex items-center gap-2 text-xs">
                    {passwordRules?.minLength ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : (
                      <XCircle className="w-4 h-4 text-gray-400" />
                    )}
                    <span className={passwordRules?.minLength ? 'text-green-700' : 'text-gray-600'}>
                      At least 8 characters
                    </span>
                  </li>
                  <li className="flex items-center gap-2 text-xs">
                    {passwordRules?.hasUpperCase ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : (
                      <XCircle className="w-4 h-4 text-gray-400" />
                    )}
                    <span
                      className={passwordRules?.hasUpperCase ? 'text-green-700' : 'text-gray-600'}
                    >
                      One uppercase letter (A-Z)
                    </span>
                  </li>
                  <li className="flex items-center gap-2 text-xs">
                    {passwordRules?.hasLowerCase ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : (
                      <XCircle className="w-4 h-4 text-gray-400" />
                    )}
                    <span
                      className={passwordRules?.hasLowerCase ? 'text-green-700' : 'text-gray-600'}
                    >
                      One lowercase letter (a-z)
                    </span>
                  </li>
                  <li className="flex items-center gap-2 text-xs">
                    {passwordRules?.hasDigit ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : (
                      <XCircle className="w-4 h-4 text-gray-400" />
                    )}
                    <span className={passwordRules?.hasDigit ? 'text-green-700' : 'text-gray-600'}>
                      One digit (0-9)
                    </span>
                  </li>
                  <li className="flex items-center gap-2 text-xs">
                    {passwordRules?.hasSpecialChar ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : (
                      <XCircle className="w-4 h-4 text-gray-400" />
                    )}
                    <span
                      className={passwordRules?.hasSpecialChar ? 'text-green-700' : 'text-gray-600'}
                    >
                      One special character (!@#$%^&*)
                    </span>
                  </li>
                </ul>
              </div>
            )}
          </div>

          {/* Confirm Password */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Confirm Password <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type={showPasswords.confirm ? 'text' : 'password'}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                onBlur={() => handleBlur('confirmPassword')}
                className={`w-full px-4 py-2.5 pr-10 border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
                  errors.confirmPassword && touched.confirmPassword
                    ? 'border-red-500 focus:ring-red-200'
                    : 'border-gray-300 focus:ring-blue-200 focus:border-blue-500'
                }`}
                placeholder="Confirm new password"
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('confirm')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPasswords.confirm ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
            {errors.confirmPassword && touched.confirmPassword && (
              <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                <XCircle className="w-4 h-4" />
                {errors.confirmPassword}
              </p>
            )}
            {formData.confirmPassword &&
              formData.confirmPassword === formData.newPassword &&
              !errors.confirmPassword && (
                <p className="mt-1.5 text-sm text-green-600 flex items-center gap-1">
                  <CheckCircle className="w-4 h-4" />
                  Passwords match
                </p>
              )}
          </div>

          {/* Buttons */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              disabled={isLoading}
              type="submit"
              className="flex-1 px-4 py-2.5 bg-blue-600 text-white mx-auto rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              {isLoading ? <ThreeDotLoading /> : 'Update Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdatePasswordModal;
